/**
 * The value half of a rule row — one editor per shape the contract allows.
 *
 * The SHAPE is chosen by the operator (that is how `ruleLeafSchema` is
 * discriminated), and the field's TYPE only refines it: `between` is two number
 * boxes on a number and two date pickers on a date; `is` is a picker of the
 * field's known values on an enum and a text box on text. Each editor writes
 * exactly the value the contract parses — `{ amount, unit }` for "in the last",
 * `{ days }` for an anniversary, `[low, high]` for a range — so the draft model
 * needs no translation step on the way to the API, and a value the editor can
 * produce is a value the server accepts (or, if half-typed, one
 * `toLeaf` reports as incomplete).
 *
 * An emptied number box is `null`, not `0`. Zero is a real threshold ("orders
 * = 0"); an empty box is a rule somebody has not finished, and the row says
 * "needs a value" rather than counting people against a zero nobody typed.
 *
 * `any_of` is the concept's chips: each chosen value a `ValueChip` with its own
 * remove button, and an `AddChip` holding the control that adds the next — a
 * select of the field's remaining values when the catalogue knows them, a text
 * box (Enter or comma adds) when it does not. Free text, because a column of
 * cities nobody declared as an enum is still a column somebody wants to filter
 * on.
 */
import { RULE_MAX_ANY_OF, type Field, type FieldType, type RuleOperator } from '@wend/shared';
import { AddChip, ValueChip } from '@wend/ui';
import { useState, type KeyboardEvent } from 'react';

import {
  RULE_DATE_CLASS,
  RULE_NUMBER_CLASS,
  RULE_SELECT_CLASS,
  RULE_TEXT_CLASS,
  RULE_UNIT_CLASS,
} from './controls';

export interface RuleValueEditorProps {
  readonly op: RuleOperator;
  readonly type: FieldType;
  /** The field, when the catalogue has it — for its label (accessible names) and its known values. */
  readonly field: Pick<Field, 'label' | 'values'> | undefined;
  readonly value: unknown;
  readonly onChange: (value: unknown) => void;
  readonly disabled?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

function numberOrNull(raw: string): number | null {
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function NumberBox({
  value,
  onChange,
  label,
  unit,
  min,
  disabled,
}: {
  value: unknown;
  onChange: (value: number | null) => void;
  label: string;
  unit?: string;
  min?: number;
  disabled?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        type="number"
        inputMode="decimal"
        className={RULE_NUMBER_CLASS}
        value={typeof value === 'number' && Number.isFinite(value) ? value : ''}
        onChange={(event) => onChange(numberOrNull(event.target.value))}
        aria-label={label}
        min={min}
        disabled={disabled}
      />
      {unit !== undefined && <span className={RULE_UNIT_CLASS}>{unit}</span>}
    </span>
  );
}

function DateBox({ value, onChange, label, disabled }: { value: unknown; onChange: (value: string) => void; label: string; disabled?: boolean }) {
  return (
    <input
      type="date"
      className={RULE_DATE_CLASS}
      value={typeof value === 'string' ? value : ''}
      onChange={(event) => onChange(event.target.value)}
      aria-label={label}
      disabled={disabled}
    />
  );
}

function AnyOfChips({
  field,
  value,
  onChange,
  disabled,
}: {
  field: Pick<Field, 'label' | 'values'> | undefined;
  value: unknown;
  onChange: (value: string[]) => void;
  disabled?: boolean;
}) {
  const label = field?.label ?? 'this field';
  const selected = Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
  const lower = new Set(selected.map((entry) => entry.toLowerCase()));
  const remaining = (field?.values ?? []).filter((option) => !lower.has(option.toLowerCase()));
  const [draft, setDraft] = useState('');
  const full = selected.length >= RULE_MAX_ANY_OF;

  function add(raw: string): void {
    const additions = raw
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry !== '');
    const next = [...selected];
    for (const entry of additions) {
      if (next.length >= RULE_MAX_ANY_OF) break;
      if (!next.some((existing) => existing.toLowerCase() === entry.toLowerCase())) next.push(entry);
    }
    if (next.length !== selected.length) onChange(next);
    setDraft('');
  }

  function onKey(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      add(draft);
    } else if (event.key === 'Backspace' && draft === '' && selected.length > 0) {
      onChange(selected.slice(0, -1));
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {selected.map((entry) => (
        <ValueChip
          key={entry}
          label={entry}
          onRemove={() => onChange(selected.filter((existing) => existing !== entry))}
          removeLabel={`Remove ${entry} from ${label}`}
        />
      ))}
      {!full && field?.values !== null && field?.values !== undefined && remaining.length > 0 && (
        <AddChip>
          <select
            className="min-h-[27px] cursor-pointer rounded-sm bg-transparent px-2 py-0.5 text-[0.78rem] text-muted [&>option]:bg-surface-2 [&>option]:text-text"
            value=""
            aria-label={`Add a value for ${label}`}
            disabled={disabled}
            onChange={(event) => {
              if (event.target.value !== '') add(event.target.value);
            }}
          >
            <option value="">＋ Add</option>
            {remaining.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </AddChip>
      )}
      {!full && (field?.values === null || field?.values === undefined) && (
        <AddChip>
          <input
            className="min-h-[27px] w-32 rounded-sm bg-transparent px-2 py-0.5 text-[0.78rem] text-text placeholder:text-muted"
            value={draft}
            placeholder="＋ Add, then Enter"
            aria-label={`Add a value for ${label}. Press Enter or type a comma to add it.`}
            disabled={disabled}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKey}
            onBlur={() => {
              if (draft.trim() !== '') add(draft);
            }}
          />
        </AddChip>
      )}
    </span>
  );
}

/**
 * The value editor for one rule. Renders nothing for an operator that takes no
 * value (`known`, `is_true`, `ever`, …).
 */
export function RuleValueEditor({ op, type, field, value, onChange, disabled }: RuleValueEditorProps) {
  const label = field?.label ?? 'this field';

  switch (op) {
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
    case 'eq':
      return <NumberBox value={value} onChange={onChange} label={`${label} value`} disabled={disabled} />;

    case 'between': {
      const pair = Array.isArray(value) ? value : [null, null];
      if (type === 'date') {
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <DateBox value={pair[0]} onChange={(low) => onChange([low, pair[1] ?? null])} label={`${label} from`} disabled={disabled} />
            <span className={RULE_UNIT_CLASS}>and</span>
            <DateBox value={pair[1]} onChange={(high) => onChange([pair[0] ?? null, high])} label={`${label} to`} disabled={disabled} />
          </span>
        );
      }
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <NumberBox value={pair[0]} onChange={(low) => onChange([low, pair[1] ?? null])} label={`${label} from`} disabled={disabled} />
          <span className={RULE_UNIT_CLASS}>and</span>
          <NumberBox value={pair[1]} onChange={(high) => onChange([pair[0] ?? null, high])} label={`${label} to`} disabled={disabled} />
        </span>
      );
    }

    case 'in_last':
    case 'not_in_last': {
      const window = isRecord(value) ? value : { amount: null, unit: 'days' };
      const unit = window.unit === 'months' ? 'months' : 'days';
      return (
        <span className="inline-flex items-center gap-1.5">
          <NumberBox
            value={window.amount}
            onChange={(amount) => onChange({ amount, unit })}
            label={`Number of ${unit} for ${label}`}
            min={1}
            disabled={disabled}
          />
          <select
            className={RULE_SELECT_CLASS}
            value={unit}
            aria-label={`Days or months, for ${label}`}
            disabled={disabled}
            onChange={(event) => onChange({ amount: window.amount ?? null, unit: event.target.value })}
          >
            <option value="days">days</option>
            <option value="months">months</option>
          </select>
        </span>
      );
    }

    case 'before':
    case 'after':
      return <DateBox value={value} onChange={onChange} label={`${label} date`} disabled={disabled} />;

    case 'anniversary_in_next': {
      const days = isRecord(value) ? value.days : null;
      return (
        <NumberBox
          value={days}
          onChange={(next) => onChange({ days: next })}
          label={`Days ahead, for the anniversary of ${label}`}
          unit="days"
          min={0}
          disabled={disabled}
        />
      );
    }

    case 'any_of':
      return <AnyOfChips field={field} value={value} onChange={onChange} disabled={disabled} />;

    case 'is':
    case 'is_not':
      if (field?.values !== null && field?.values !== undefined && field.values.length > 0) {
        const current = typeof value === 'string' ? value : '';
        return (
          <select
            className={RULE_SELECT_CLASS}
            value={current}
            aria-label={`${label} value`}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
          >
            {current === '' && <option value="">Choose…</option>}
            {/* A saved value no longer among the options is still shown, so the row says what it holds. */}
            {current !== '' && !field.values.includes(current) && <option value={current}>{current}</option>}
            {field.values.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      }
      return <TextBox value={value} onChange={onChange} label={`${label} value`} disabled={disabled} />;

    case 'contains':
      return <TextBox value={value} onChange={onChange} label={`Text ${label} contains`} disabled={disabled} />;

    default:
      return null;
  }
}

function TextBox({ value, onChange, label, disabled }: { value: unknown; onChange: (value: string) => void; label: string; disabled?: boolean }) {
  return (
    <input
      className={RULE_TEXT_CLASS}
      value={typeof value === 'string' ? value : ''}
      onChange={(event) => onChange(event.target.value)}
      aria-label={label}
      placeholder="value"
      disabled={disabled}
    />
  );
}
